/**
 * FEEDBACK WIDGET — DEBUG CAPTURE (TICKET-CRM-14)
 * Intercepte console.error/warn/log et assemble le snapshot debug complet.
 * Port de benevoles ; la capture zustand est remplacée par collectContext().
 */

import type {
  ConsoleEntry,
  ConsoleEntryType,
  DebugSnapshot,
  DeviceMetadata,
  NavigationInfo,
} from './feedback.types';
import { collectContext } from './context-registry';
import { networkTracker } from './network-tracker';
import { userActionsTracker } from './user-actions-tracker';

const MAX_CONSOLE_ENTRIES = 50;
const MAX_MESSAGE_LENGTH = 2000;
const MAX_STACK_LENGTH = 5000;

class DebugCaptureService {
  private consoleLogs: ConsoleEntry[] = [];
  private isInitialized = false;
  private originalConsole: {
    error: typeof console.error;
    warn: typeof console.warn;
    log: typeof console.log;
  } | null = null;

  initialize(): void {
    if (this.isInitialized || typeof window === 'undefined') {
      return;
    }

    this.originalConsole = {
      error: console.error,
      warn: console.warn,
      // eslint-disable-next-line no-console -- interception volontaire : on wrappe console.log pour le snapshot debug
      log: console.log,
    };

    console.error = (...args: unknown[]) => {
      this.captureEntry('error', args);
      this.originalConsole?.error(...args);
    };
    console.warn = (...args: unknown[]) => {
      this.captureEntry('warn', args);
      this.originalConsole?.warn(...args);
    };
    // eslint-disable-next-line no-console -- interception volontaire (voir ci-dessus)
    console.log = (...args: unknown[]) => {
      this.captureEntry('log', args);
      this.originalConsole?.log(...args);
    };

    userActionsTracker.initialize();
    networkTracker.initialize();

    this.isInitialized = true;
  }

  destroy(): void {
    if (!this.isInitialized || !this.originalConsole) {
      return;
    }

    console.error = this.originalConsole.error;
    console.warn = this.originalConsole.warn;
    // eslint-disable-next-line no-console -- restauration de la méthode originale
    console.log = this.originalConsole.log;

    userActionsTracker.destroy();
    networkTracker.destroy();

    this.originalConsole = null;
    this.isInitialized = false;
  }

  private captureEntry(type: ConsoleEntryType, args: unknown[]): void {
    const entry: ConsoleEntry = {
      type,
      message: this.formatMessage(args),
      timestamp: new Date().toISOString(),
    };

    if (type === 'error') {
      const errorArg = args.find((arg) => arg instanceof Error);
      if (errorArg instanceof Error && errorArg.stack) {
        entry.stack = errorArg.stack.slice(0, MAX_STACK_LENGTH);
      }
    }

    this.consoleLogs.push(entry);
    if (this.consoleLogs.length > MAX_CONSOLE_ENTRIES) {
      this.consoleLogs.shift();
    }
  }

  private formatMessage(args: unknown[]): string {
    return args
      .map((arg) => this.stringify(arg))
      .join(' ')
      .slice(0, MAX_MESSAGE_LENGTH);
  }

  private stringify(value: unknown): string {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (value instanceof Error) return value.message;
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value).slice(0, 500);
      } catch {
        return '[Object]';
      }
    }
    return String(value);
  }

  private captureNavigation(): NavigationInfo {
    const searchParams: Record<string, string> = {};
    new URLSearchParams(window.location.search).forEach((value, key) => {
      searchParams[key] = value;
    });

    return {
      url: window.location.href,
      pathname: window.location.pathname,
      searchParams,
    };
  }

  private captureDeviceMetadata(): DeviceMetadata {
    return {
      timestamp: new Date().toISOString(),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
    };
  }

  captureSnapshot(): DebugSnapshot {
    return {
      console: [...this.consoleLogs],
      state: collectContext(),
      navigation: this.captureNavigation(),
      metadata: this.captureDeviceMetadata(),
      userActions: userActionsTracker.getActions(),
      networkRequests: networkTracker.getRequests(),
    };
  }

  isActive(): boolean {
    return this.isInitialized;
  }
}

export const debugCaptureService = new DebugCaptureService();
